import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import AirplaneTakeoff from "@/shared/assets/images/AirplaneTakeoff.svg"
import AirplaneLanding from "@/shared/assets/images/AirplaneLanding.svg"
import Users from "@/shared/assets/images/UsersThree.svg"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { fetchFlights } from "@/entities/flight"
import { morph } from "@/shared/lib/word-morph"
import Dropdown from "rc-dropdown"
import { FlightOptionsDropdownOverlay } from "../flight-options-dropdown-overlay"
import useOutsideAlerter from "@/shared/lib/use-outside-alerter"
import { ClassOfServiceNames, selectAirports, selectAirportsLoading, AvailablePassengerTypes, AvailableClassOfService } from "@/entities/aviabooking-reference"
import { AirportSelect } from "./airport-select"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { IPassenger, TClassOfService, PassengerType } from "@/shared/api/aviabooking"
import { ReturnDatePicker } from "./return-date-picker"

interface TicketsSearchProps { }

type PassengersMap = Record<string, IPassenger>

type SearchFormValues = {
    from: string
    dest: string
    date: Date | null
    backDate: Date | null
    passengersCount: number
    passengers: PassengersMap
    classOfService: TClassOfService
}

const validationSchema = yup.object().shape({
    from: yup.string().required("Укажите город отправления"),
    dest: yup
        .string()
        .required("Укажите город прибытия")
        .test("not-equal", "Города не должны совпадать", function (value) {
            const { from } = this.parent as SearchFormValues
            return value && from ? value !== from : true
        }),
    date: yup
        .date()
        .typeError("Укажите корректную дату")
        .required("Укажите дату вылета")
        .min(new Date(new Date().setHours(0, 0, 0, 0)), "Дата не может быть в прошлом"),
    backDate: yup
        .date()
        .nullable()
        .transform((value, originalValue) => originalValue === null ? null : value)
        .min(yup.ref('date'), 'Обратная дата не может быть раньше даты вылета')
        .notRequired(),
    passengersCount: yup
        .number()
        .min(1, "Должен быть хотя бы один пассажир")
        .required(),
    passengers: yup
        .object()
        .test("has-adult-with-infant", "Укажите хотя бы 1 взрослого", function (value) {
            const passengers = value as PassengersMap
            if (!passengers || typeof passengers !== 'object') {
                return true
            }

            const passengersArray = Object.values(passengers)
            if (!Array.isArray(passengersArray) || passengersArray.length === 0) {
                return true
            }

            const hasInfant = passengersArray.some(
                (p) =>
                    p.type &&
                    p.type.toLowerCase() === PassengerType.INF.toLowerCase() &&
                    p.count > 0,
            )

            if (!hasInfant) {
                return true
            }

            const hasAdult = passengersArray.some(
                (p) =>
                    p.type &&
                    p.type.toLowerCase() === PassengerType.ADT.toLowerCase() &&
                    p.count > 0,
            )

            return hasAdult
        }),

})

const FieldErrorTooltip: FC<{ error?: string, children: ReactNode, className?: string }> = ({ error, children, className }) => {
    return (
        <div className={`relative w-full ${className}`}>
            {children}
            {error && (
                <div className="absolute top-2 right-2 group">
                    <div className="w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none flex items-center justify-center outline-none">i</div>
                    <div className="absolute top-0 right-6 z-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 pointer-events-none">
                        <div className="bg-red-600 text-white text-xs px-3 py-2 rounded-md shadow-lg whitespace-nowrap">{error}</div>
                        <div className="w-2 h-2 bg-red-600 rotate-45 absolute top-1/2 right-0 -translate-y-1/2"></div>
                    </div>
                </div>
            )}
        </div>
    )
}

export const TicketsSearch: FC<TicketsSearchProps> = () => {
    const dispatch = useAppDispatch()
    const airports = useAppSelector(selectAirports)
    const isAirportsLoading = useAppSelector(selectAirportsLoading)

    const airportsSelectOptions = useMemo(() => {
        return airports.map((airport) => ({
            airport,
            value: airport.code,
            label: `${airport.city}${airport.name ? ` (${airport.name})` : ''}`
        }))
    }, [airports])

    const onSubmit = (data: SearchFormValues) => {
        const passengersArray = Object.values(data.passengers)
        dispatch(fetchFlights({ from: data.from, dest: data.dest, date: data.date as Date, backDate: data.backDate ?? null, passengers: passengersArray, classOfService: data.classOfService }))
    }

    const [overlayOpen, setOverlayOpen] = useState<boolean>(false)
    const overlayRef = useRef<HTMLDivElement>(null)
    useOutsideAlerter(overlayRef, () => setOverlayOpen(false))

    const defaultPassengers: PassengersMap = AvailablePassengerTypes.reduce((acc, type) => {
        acc[type] = { type, count: type === 'ADT' ? 1 : 0 }
        return acc
    }, {} as PassengersMap)

    const methods = useForm<SearchFormValues>({
        defaultValues: {
            from: '',
            dest: '',
            date: null,
            backDate: null,
            passengersCount: 1,
            passengers: defaultPassengers,
            classOfService: AvailableClassOfService[0]
        },
        resolver: yupResolver(validationSchema) as any,
        mode: "onSubmit"
    })

    const totalPassengers = methods.watch('passengersCount')

    useEffect(() => {
        methods.setValue("passengersCount", totalPassengers, { shouldValidate: true })
    }, [totalPassengers])

    return (
        <div className="w-full flex gap-4">
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full">
                    <div className="w-full">
                        <div className="w-full grid grid-cols-12">
                            <FieldErrorTooltip className="col-span-6 xxl:col-span-2" error={methods.formState.errors.from?.message as string}>
                                <div className={`flex shadow-sm min-h-[84px] bg-white rounded-tl-3xl xxl:rounded-l-3xl border-b xxl:border-b-0 w-full px-3 border-r overflow-hidden`}>
                                    <div className="flex items-center shrink-0 px-2 text-color-primary">
                                        <AirplaneTakeoff width={26} height={26} />
                                    </div>
                                    <div className="flex-col flex w-full justify-center">
                                        <div className="text-zinc-400">
                                            Откуда
                                        </div>
                                        <Controller
                                            name="from"
                                            control={methods.control}
                                            render={({ field }) => (
                                                <AirportSelect
                                                    onChange={(e) => {
                                                        field.onChange(e.value)
                                                    }}
                                                    options={airportsSelectOptions}
                                                    menuPortalTarget={document.body}
                                                    value={airportsSelectOptions.filter((v) => v.value === field.value)}
                                                    isLoading={isAirportsLoading}
                                                    placeholder="Город отправления"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </FieldErrorTooltip>
                            <FieldErrorTooltip className="col-span-6 xxl:col-span-2" error={methods.formState.errors.dest?.message as string}>
                                <div className={`flex shadow-sm min-h-[84px] bg-white rounded-tr-3xl xxl:rounded-tr-none border-b xxl:border-b-0 w-full px-3 border-r-0 xxl:border-r overflow-hidden`}>
                                    <div className="flex items-center shrink-0 px-2 text-color-primary">
                                        <AirplaneLanding width={26} height={26} />
                                    </div>
                                    <div className="flex-col flex w-full justify-center">
                                        <div className="text-zinc-400">
                                            Куда
                                        </div>
                                        <Controller
                                            name="dest"
                                            control={methods.control}
                                            render={({ field }) => (
                                                <AirportSelect
                                                    onChange={(e) => {
                                                        field.onChange(e.value)
                                                    }}
                                                    options={airportsSelectOptions}
                                                    menuPortalTarget={document.body}
                                                    value={airportsSelectOptions.filter((v) => v.value === field.value)}
                                                    isLoading={isAirportsLoading}
                                                    placeholder="Город прибытия"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </FieldErrorTooltip>
                            <FieldErrorTooltip className="col-span-3 xxl:col-span-2" error={methods.formState.errors.date?.message as string}>
                                <div className={`flex shadow-sm min-h-[84px] bg-white rounded-bl-3xl xxl:rounded-bl-none w-full px-3 border-r justify-center flex-col overflow-hidden`}>
                                    <div className="pb-2 text-zinc-400">
                                        Туда
                                    </div>
                                    <div className="mx-4">
                                        <div className="px-4 py-1 w-[116px] rounded-lg overflow-hidden bg-zinc-100">
                                            <Controller
                                                name="date"
                                                control={methods.control}
                                                render={({ field }) => (
                                                    <DatePicker
                                                        placeholderText={"Когда"}
                                                        selected={field.value ?? null}
                                                        wrapperClassName="react-datepicker-avia"
                                                        dateFormat={"dd.MM.yyyy"}
                                                        showIcon
                                                        minDate={new Date()}
                                                        onChange={(d: Date) => {
                                                            field.onChange(d)
                                                        }}
                                                        enableTabLoop={false}
                                                        popperProps={{
                                                            placement: 'bottom',
                                                        }}
                                                        icon={null}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </FieldErrorTooltip>
                            <FieldErrorTooltip className="col-span-3 xxl:col-span-2" error={methods.formState.errors.backDate?.message as string}>
                                <div className={`flex shadow-sm min-h-[84px] bg-white w-full px-3 border-r justify-center flex-col overflow-hidden`}>
                                    <div className="pb-2 text-zinc-400">
                                        Обратно
                                    </div>
                                    <div className="mx-4">
                                        <div className="px-2 py-1 w-[140px] rounded-lg bg-zinc-100">
                                            <Controller
                                                name="backDate"
                                                control={methods.control}
                                                render={({ field }) => (
                                                    <ReturnDatePicker
                                                        value={field.value}
                                                        minDate={methods.getValues('date') ?? new Date()}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </FieldErrorTooltip>
                            <FieldErrorTooltip 
                                className="col-span-3 xxl:col-span-2" 
                                error={(
                                    methods.formState.errors.passengersCount?.message || 
                                    methods.formState.errors.passengers?.message
                                ) as string}
                                >
                                <Dropdown
                                    trigger={['click']}
                                    visible={overlayOpen}
                                    overlay={(
                                        <FlightOptionsDropdownOverlay ref={overlayRef} />
                                    )}
                                >
                                    <div
                                        onClick={() => setOverlayOpen(true)}
                                        className="flex shadow-sm transition-all hover:cursor-pointer min-h-[84px] bg-white w-full xxl:rounded-r-3xl px-3 overflow-hidden"
                                    >
                                        <div className="flex items-center px-2 text-color-primary">
                                            <Users width={26} height={26} />
                                        </div>
                                        <div className="flex-col flex justify-center">
                                            <div className="pb-1 text-zinc-400">
                                                {totalPassengers} {morph(totalPassengers, ["пассажир", "пассажира", "пассажиров"])}
                                            </div>
                                            <div className="text-zinc-400">
                                                {ClassOfServiceNames[methods.watch('classOfService') as TClassOfService].title}
                                            </div>
                                        </div>
                                    </div>
                                </Dropdown>
                            </FieldErrorTooltip>
                            <div className="col-span-3 xxl:col-span-2">
                                <button type="submit" className="w-full ml-3 h-full hidden xxl:block text-xl px-6 bg-color-primary text-white rounded-3xl p-3 shrink-0 text-center font-bold">
                                    Найти билеты
                                </button>
                                <div className="bg-white min-h-[84px h-full rounded-br-3xl overflow-hidden shadow-sm block xxl:hidden">
                                    <button type="submit" className="w-full rounded-tl-3xl h-full text-xl px-6 bg-color-primary text-white p-3 shrink-0 text-center font-bold">
                                        Найти билеты
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}